import { z } from 'zod'

import { ModuleTypeEnum } from './module-type'

// Define the feature flag keys and their paths - MVP version
export const FeatureFlagKey = {
  // Core
  PROJECTS: { key: 'projects', paths: ['project', 'projects'] },
  TASKS: { key: 'tasks', paths: ['task', 'tasks'] },
  // Reusable task templates (client onboarding, monthly reporting, site
  // launch): a saved subtask tree + checklists that instantiate into real
  // tasks. Business-shaped repeatable delivery work — OFF for personal.
  TASK_TEMPLATES: {
    key: 'task_templates',
    paths: ['task-template', 'task-templates'],
  },
  FOCUS_MODE: { key: 'focus_mode', paths: ['focus'] },
  TICKETS: { key: 'tickets', paths: ['ticket', 'tickets'] },
  CALENDAR: { key: 'calendar', paths: ['calendar'] },
  NOTES: { key: 'notes', paths: ['note', 'notes'] },

  // Sales
  DEALS: { key: 'deals', paths: ['deal', 'deals'] },
  CONTACTS: { key: 'contacts', paths: ['contact', 'contacts'] },
  MARKETING: {
    key: 'marketing',
    paths: ['marketing', 'campaign', 'campaigns', 'journey', 'journeys'],
  },
  SEQUENCES: { key: 'sequences', paths: ['sequence', 'sequences'] },
  SCHEDULING: { key: 'scheduling', paths: ['scheduling', 'booking'] },
  WEB_TRACKING: { key: 'web_tracking', paths: ['tracking'] },
  LIVE_CHAT: { key: 'live_chat', paths: ['live-chat'] },
  QUOTES: { key: 'quotes', paths: ['quote', 'quotes'] },
  // Sell-side product/service price book (its own module; referenced by
  // quote and invoice line items, synced from accounting apps).
  PRODUCTS: { key: 'products', paths: ['product', 'products'] },
  KNOWLEDGE_BASE: { key: 'knowledge_base', paths: ['knowledge-base'] },
  CALLING: { key: 'calling', paths: ['communications', 'calls'] },
  ENRICHMENT: { key: 'enrichment', paths: ['data-quality'] },

  // Operations
  TEAM: { key: 'team', paths: ['team'] },
  TIME_TRACKING: {
    key: 'time_tracking',
    paths: ['time-entry', 'time-entries', 'timesheet', 'timesheets'],
  },
  ENTITIES: { key: 'entities', paths: ['entity', 'entities'] },
  CONTRACTS: { key: 'contracts', paths: ['contract', 'contracts'] },
  INVENTORY: { key: 'inventory', paths: ['inventory'] },
  SUPPLIERS: { key: 'suppliers', paths: ['supplier', 'suppliers'] },
  WORKFLOWS: {
    key: 'workflows',
    paths: ['workflow', 'workflows', 'automation', 'automations'],
  },
  WORKSTREAM: {
    key: 'workstream',
    paths: ['workstream', 'workstreams'],
  },

  // Finance
  INVOICES: { key: 'invoices', paths: ['invoice', 'invoices', 'invoicing'] },
  // Profitability module (org P&L, forecast, scenarios, rates, earnings).
  // Display name is "Profitability"; internal key stays financials.
  FINANCIALS: { key: 'financials', paths: ['financials', 'profitability'] },

  // Events
  EVENTS: { key: 'events', paths: ['event', 'events'] },

  // Other
  FORMS: { key: 'forms', paths: ['form', 'forms'] },
  COLLECTIONS: { key: 'collections', paths: ['collection', 'collections'] },
  // Date-field reminder rules on collections (bills due dates, renewals…).
  // Generic capability: useful to both work orgs and personal spaces.
  COLLECTION_REMINDERS: {
    key: 'collection_reminders',
    paths: ['collection-reminders'],
  },
  // Birthday field on contacts + daily reminder notifications.
  CONTACT_BIRTHDAYS: {
    key: 'contact_birthdays',
    paths: ['contact-birthdays'],
  },
  // Public share links for list projects (family lists shared with guests).
  LIST_SHARING: {
    key: 'list_sharing',
    paths: ['list-sharing'],
  },
  // Weekly meal planner grid + recipe URL import.
  MEAL_PLANNER: {
    key: 'meal_planner',
    paths: ['meals', 'meal-planner'],
  },
  DISCUSSIONS: {
    key: 'discussions',
    paths: ['thread', 'threads', 'discussion', 'discussions'],
  },
  DOCUMENTS: { key: 'documents', paths: ['document', 'documents'] },
  AI_FEATURES: { key: 'ai_features', paths: ['ai', 'assistant'] },
  MEETING_NOTES: { key: 'meeting_notes', paths: ['meeting', 'meetings'] },
  ADVANCED_REPORTS: {
    key: 'advanced_reports',
    paths: ['report', 'reports', 'analytics'],
  },
  CLIENT_HEALTH: {
    key: 'client_health',
    paths: ['health', 'health-triage', 'command-center'],
  },

  // System
  SETTINGS: { key: 'settings', paths: ['setting', 'settings', 'preferences'] },
  SUBSCRIPTION: {
    key: 'subscription',
    paths: ['subscription', 'billing', 'plan'],
  },

  // Integrations
  SLACK_INTEGRATION: { key: 'slack_integration', paths: ['slack'] },

  // Simple Mode: chat-first workspace (conversations with Benny + an adaptive
  // canvas) served at /:orgSlug/simple, outside the normal sidebar shell.
  // In development — global flag ships disabled; enable per-org to test.
  SIMPLE_MODE: { key: 'simple_mode', paths: ['simple'] },

  // Platform rollout flags (not modules) — gate global capabilities, not routes.
  // PERSONAL_ACCOUNTS controls whether self-serve personal-account creation is
  // available (checked globally in onboarding, before the user has an org).
  PERSONAL_ACCOUNTS: {
    key: 'personal_accounts',
    paths: ['personal-accounts'],
  },
} as const

export type FeatureFlagKeyType = keyof typeof FeatureFlagKey

/**
 * The feature-flag profile applied to a Personal account.
 *
 * This is the SINGLE SOURCE OF TRUTH for personal-account module visibility and
 * for account-type conversions in both directions:
 *  - creating a PERSONAL org seeds OrganizationFeatureFlag rows from this map;
 *  - converting personal → organization re-enables exactly the flags this map
 *    disables; converting organization → personal re-applies this map.
 *
 * Keyed by every FeatureFlagKeyType so the compiler enforces exhaustiveness.
 * Personal accounts are free by construction (SUBSCRIPTION is OFF — no billing).
 */
export const PERSONAL_ACCOUNT_FLAG_PROFILE: Record<
  FeatureFlagKeyType,
  boolean
> = {
  // Core personal life-management modules — ON.
  PROJECTS: true,
  TASKS: true,
  CALENDAR: true,
  NOTES: true,
  DOCUMENTS: true,
  CONTACTS: true,
  TEAM: true, // simplified as Members for personal spaces
  // Vendors retired for personal (2026-07-17 stakeholder decision): the
  // Address Book absorbs the plumber/electrician role. Conversion to a
  // business org restores Suppliers.
  SUPPLIERS: false,
  EVENTS: true,
  COLLECTIONS: true,
  COLLECTION_REMINDERS: true, // bills due dates, renewals
  CONTACT_BIRTHDAYS: true, // family birthday reminders
  LIST_SHARING: true, // share a family list with guests via link
  MEAL_PLANNER: true, // weekly meal grid + recipe URL import
  AI_FEATURES: true,
  SETTINGS: true,
  // Benny Focus mode is work-only for now (AI credits are a business
  // concept and personal spaces have no credit ledger).
  FOCUS_MODE: false,
  // Simple Mode is work-only while in development (chat-first shell over
  // business modules; revisit for personal once the canvas covers
  // personal-mode record types).
  SIMPLE_MODE: false,
  // Task templates are professional-services delivery machinery (repeatable
  // client onboarding / reporting engagements). A family space repeats
  // chores through recurring tasks, not through saved work breakdowns.
  TASK_TEMPLATES: false,
  MARKETING: false,
  SEQUENCES: false,
  SCHEDULING: false,
  WEB_TRACKING: false,
  LIVE_CHAT: false,
  QUOTES: false,
  PRODUCTS: false,
  KNOWLEDGE_BASE: false,
  CALLING: false,
  ENRICHMENT: false,

  // Business modules — OFF (hidden in personal mode, restored on conversion).
  TICKETS: false,
  DEALS: false,
  ENTITIES: false,
  CONTRACTS: false,
  INVOICES: false,
  FINANCIALS: false, // a family account has no P&L

  TIME_TRACKING: false,
  INVENTORY: false,
  FORMS: false,
  WORKFLOWS: false,
  WORKSTREAM: false,
  DISCUSSIONS: false,
  MEETING_NOTES: false,
  ADVANCED_REPORTS: false,
  CLIENT_HEALTH: false,
  SUBSCRIPTION: false,
  SLACK_INTEGRATION: false,

  // Platform rollout flag, not a module — its per-org value is irrelevant
  // (personal-account creation is gated globally, not per-org). Present only
  // to keep this Record<FeatureFlagKeyType, boolean> exhaustive.
  PERSONAL_ACCOUNTS: true,
}

/**
 * Maps a permission-catalog module (ModuleTypeEnum) to the feature flag that
 * gates its availability. Used to hide permission CATEGORIES in the permission-
 * set editor when the underlying module is turned off for an organization
 * (personal accounts have most business modules off; a business org may also
 * disable modules). Grantable modules that are structural / always-available
 * (My Organization, Integrations, Members, custom fields) — and the core
 * personal modules the product treats as always-on (Projects, Tasks, Notes) —
 * are intentionally absent, so they are never hidden.
 *
 * Gating by feature flag (not by isPersonal) means this automatically does the
 * right thing for both personal accounts and business orgs that disabled a
 * module. `undefined` (module not in this map) = always shown.
 */
export const MODULE_TYPE_FEATURE_FLAG: Partial<
  Record<ModuleTypeEnum, FeatureFlagKeyType>
> = {
  [ModuleTypeEnum.CALENDAR]: 'CALENDAR',
  [ModuleTypeEnum.EVENT]: 'EVENTS',
  [ModuleTypeEnum.ENTITY]: 'ENTITIES',
  [ModuleTypeEnum.CONTACT]: 'CONTACTS',
  [ModuleTypeEnum.SUPPLIER]: 'SUPPLIERS',
  [ModuleTypeEnum.DOCUMENT]: 'DOCUMENTS',
  [ModuleTypeEnum.CUSTOM_COLLECTION]: 'COLLECTIONS',
  [ModuleTypeEnum.AI]: 'AI_FEATURES',
  // Business modules — off in the personal profile, hidden in the editor there.
  [ModuleTypeEnum.TICKET]: 'TICKETS',
  [ModuleTypeEnum.TIME_ENTRY]: 'TIME_TRACKING',
  [ModuleTypeEnum.EXPENSE]: 'TIME_TRACKING',
  [ModuleTypeEnum.THREAD]: 'DISCUSSIONS',
  [ModuleTypeEnum.DEAL]: 'DEALS',
  [ModuleTypeEnum.TEAM]: 'TEAM',
  [ModuleTypeEnum.INVENTORY]: 'INVENTORY',
  [ModuleTypeEnum.CONTRACT]: 'CONTRACTS',
  [ModuleTypeEnum.INVOICE]: 'INVOICES',
  [ModuleTypeEnum.QUOTE]: 'QUOTES',
  [ModuleTypeEnum.BILLING_AUTHORIZATION]: 'INVOICES',
  [ModuleTypeEnum.FORM]: 'FORMS',
  [ModuleTypeEnum.WORKFLOW]: 'WORKFLOWS',
  [ModuleTypeEnum.WORKSTREAM]: 'WORKSTREAM',
  [ModuleTypeEnum.ANALYTICS]: 'ADVANCED_REPORTS',
  [ModuleTypeEnum.MARKETING]: 'MARKETING',
  [ModuleTypeEnum.KNOWLEDGE_BASE]: 'KNOWLEDGE_BASE',
  [ModuleTypeEnum.FINANCIALS]: 'FINANCIALS',
}

/**
 * Module → subscription feature key, mirroring the `subscriptionFeature`
 * values on the corresponding sidebar nav items. Used by admin surfaces
 * (e.g. the permission editor) to hide modules the org's plan doesn't
 * include. `undefined` = not subscription-gated. Personal accounts are
 * unaffected: their subscription layer is inert (checkFeature → true) and
 * feature flags remain the real gate.
 */
export const MODULE_TYPE_SUBSCRIPTION_FEATURE: Partial<
  Record<ModuleTypeEnum, string>
> = {
  [ModuleTypeEnum.TICKET]: 'tickets',
  [ModuleTypeEnum.CUSTOM_COLLECTION]: 'collections',
  [ModuleTypeEnum.FORM]: 'forms',
  [ModuleTypeEnum.THREAD]: 'discussions',
  [ModuleTypeEnum.EVENT]: 'events',
  [ModuleTypeEnum.SUPPLIER]: 'suppliers',
  [ModuleTypeEnum.TEAM]: 'team',
  [ModuleTypeEnum.WORKFLOW]: 'workflows',
  [ModuleTypeEnum.DEAL]: 'deals',
  [ModuleTypeEnum.ENTITY]: 'clients',
  [ModuleTypeEnum.CONTRACT]: 'contracts',
  [ModuleTypeEnum.QUOTE]: 'quotes',
  [ModuleTypeEnum.INVOICE]: 'invoices',
  [ModuleTypeEnum.BILLING_AUTHORIZATION]: 'billing_schedules',
  [ModuleTypeEnum.EXPENSE]: 'expenses',
  [ModuleTypeEnum.KNOWLEDGE_BASE]: 'knowledge_base',
  [ModuleTypeEnum.PERMISSION_SET]: 'custom_permission_sets',
  [ModuleTypeEnum.ANALYTICS]: 'analytics',
  [ModuleTypeEnum.FINANCIALS]: 'profitability',
  [ModuleTypeEnum.MARKETING]: 'marketing',
}

// Error codes for feature flag operations
export const FeatureFlagErrorCode = {
  NOT_FOUND: 'FEATURE_FLAG_NOT_FOUND',
  INVALID_KEY: 'INVALID_FEATURE_FLAG_KEY',
  UNAUTHORIZED: 'UNAUTHORIZED_FEATURE_FLAG_ACCESS',
  SERVER_ERROR: 'FEATURE_FLAG_SERVER_ERROR',
} as const

export type FeatureFlagErrorCode =
  (typeof FeatureFlagErrorCode)[keyof typeof FeatureFlagErrorCode]

// Helper function to get feature flag from path
export function getFeatureFlagFromPath(
  path: string,
): FeatureFlagKeyType | undefined {
  const normalizedPath = path.toLowerCase()

  for (const [key, value] of Object.entries(FeatureFlagKey)) {
    if (value.paths.some(p => normalizedPath.includes(p))) {
      return key as FeatureFlagKeyType
    }
  }

  return undefined
}

// Zod schemas for validation
const BaseFeatureFlagSchema = z.object({
  id: z.string().optional(),
  key: z.string().optional(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  isEnabled: z.boolean().optional(),
  config: z
    .union([
      z.record(z.unknown()),
      z.null(),
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.any()),
    ])
    .optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})

const OrganizationFeatureFlagSchema = z.object({
  id: z.string().optional(),
  organizationId: z.string().optional(),
  featureFlagId: z.string().optional(),
  isEnabled: z.boolean().optional(),
  config: z
    .union([
      z.record(z.unknown()),
      z.null(),
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.any()),
    ])
    .optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  featureFlag: BaseFeatureFlagSchema.optional(),
  organization: z
    .object({
      id: z.string().optional(),
      name: z.string().optional(),
      slug: z.string().optional(),
    })
    .optional(),
})

export type FeatureFlag = z.infer<typeof BaseFeatureFlagSchema>
export type OrganizationFeatureFlag = z.infer<
  typeof OrganizationFeatureFlagSchema
>
